import axios from 'axios';
import {
    ActiveDirectoryCode,
    ActiveDirectoryConnectionCode,
    ActiveDirectoryGroup,
    ActiveDirectoryGroupRoles,
    ActiveDirectoryProperties,
    AD_GROUP_ROLE,
    ConfigurationEntry,
    Model,
    SharePointPropertiesData,
    SharePointPropertiesId
} from "../models/admin-config.models";

export const DEFAULT_AD_FORM_VALUES: ActiveDirectoryProperties = {
    baseDn: '',
    code: '',
    groupMemberAttribute: '',
    groupNameAttribute: '',
    groupSearchBase: '',
    memberOfAttribute: '',
    serviceAccountDn: '',
    serviceAccountPassword: '',
    url: '',
    userLoginAttribute: '',
    userObjectClass: '',
    userSearchBase: '',
    nestedGroupsEnabled: false,
    certificateFileName: '',
    certificateContent: '',
};

export const DEFAULT_SHAREPOINT_FORM_VALUES: SharePointPropertiesData = {
    tenantId: '',
    maxFileSizeBytes: 0,
    clientId: '',
    clientSecret: '',
    siteId: '',
    driveId: '',
};

export const adminConfigurationService = {
    fetchGeneralConfig: async (): Promise<ConfigurationEntry[]> => {
        const res = await axios.get<ConfigurationEntry[]>(`/config`);
        return res.data;
    },

    fetchConfigPerName: async (name: string): Promise<string> => {
        const response = await axios.get<string>(`/config/${name}`);
        return response.data;
    },

    setConfig: async (name: string, value: string): Promise<void> => {
        await axios.put(`/config`, { propertyName: name, value });
    },

    getDefaultModelConfig: async (): Promise<Model> => {
        const res = await axios.get<Model>(`/models/default `);
        return res.data;
    },

    setDefaultModelConfig: async (modelId: number): Promise<void> => {
        await axios.patch(`/models/default/update/${modelId}`);
    },

    getModelsList: async (): Promise<Model[]> => {
        const res = await axios.get<Model[]>(`/models `);
        return res.data;
    },

    setActiveDirectoryConnectionConfig: async (configuration: ActiveDirectoryProperties): Promise<ActiveDirectoryConnectionCode> => {
        const res = await axios.post<ActiveDirectoryConnectionCode>(`/ad/properties`, configuration);
        return res.data;
    },

    fetchActiveDirectoryConnectionConfig: async (code: string): Promise<ActiveDirectoryProperties> => {
        const res = await axios.get<ActiveDirectoryProperties>(`/ad/properties/${code}`);
        return res.data;
    },

    fetchActiveDirectoryCode: async (): Promise<ActiveDirectoryCode> => {
        const res = await axios.get<ActiveDirectoryCode>(`/ad/properties/code`);
        return res.data;
    },

    updateActiveDirectoryGroupsWithConnectionConfig: async (code: string): Promise<void> => await axios.post(`/ad/properties/${code}/groups`),

    fetchActiveDirectoryGroups: async (): Promise<ActiveDirectoryGroup[]> => {
        const res = await axios.get<ActiveDirectoryGroup[]>(`/ad/groups`);
        return res.data;
    },

    fetchActiveDirectoryGroupRoles: async (): Promise<ActiveDirectoryGroupRoles> => {
        const res = await axios.get<ActiveDirectoryGroupRoles>(`/ad/group-roles`);
        return res.data;
    },

    updateActiveDirectoryGroupRoles: async (code: string, assignments: {groupId: number, role: AD_GROUP_ROLE | ''}[]): Promise<void> => {
        const requestBody = {
            providerCode: code,
            assignments: assignments,
        };
        await axios.post(`/ad/assign-role`, requestBody);
    },

    fetchSharePointProperties: async (): Promise<SharePointPropertiesData> => {
        const response = await axios.get<SharePointPropertiesData>(`/sharepoint/properties`);
        return response.data;
    },

    setSharePointProperties: async (properties: SharePointPropertiesData): Promise<void> => {
        await axios.post<SharePointPropertiesId>(`/sharepoint/properties`, properties);
    }
};